/**
 * Calcula o "período comparativo" para uma tabela comparativa do dashboard,
 * com base no filtro ativo.
 *
 * Regras:
 *  - today               → ontem
 *  - week (últimos 7d)   → 7 dias imediatamente anteriores
 *  - month (últimos 30d) → 30 dias imediatamente anteriores
 *  - custom              → mesmo período do ano anterior (sempre, independente da duração)
 *
 * Retorna `null` quando não há range válido (ex.: período custom sem datas).
 */
import type { PeriodType } from '../components/FilterPanel';

export interface DateRange {
  start: string; // YYYY-MM-DD
  end: string;   // YYYY-MM-DD
}

export interface ComparativeRange {
  current: DateRange;
  previous: DateRange;
  /** Texto curto descrevendo a estratégia ("Dia anterior", "7 dias anteriores", "Mesmo período do ano anterior"). */
  label: string;
  /** Se a comparação é ano-a-ano (custom >= 365 dias). */
  isYearOverYear: boolean;
}

const fmt = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parse = (s: string): Date => {
  // ISO YYYY-MM-DD → local midday (evita TZ flip)
  return new Date(s + 'T12:00:00');
};

const todayLocal = (): Date => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

/**
 * Calcula a duração em dias (inclusivo).
 */
const diffDays = (start: Date, end: Date): number => {
  const ms = end.getTime() - start.getTime();
  return Math.round(ms / 86400000) + 1;
};

export function computeComparativeRange(
  period: PeriodType,
  startDate?: string,
  endDate?: string,
): ComparativeRange | null {
  let curStart: Date;
  let curEnd: Date;

  if (period === 'today') {
    curStart = todayLocal();
    curEnd = todayLocal();
  } else if (period === 'week') {
    curEnd = todayLocal();
    curStart = new Date(curEnd);
    curStart.setDate(curStart.getDate() - 7); // 8 dias inclusivo, igual ao backend (today-7 a today)
  } else if (period === 'month') {
    curEnd = todayLocal();
    curStart = new Date(curEnd);
    curStart.setDate(curStart.getDate() - 30); // 31 dias inclusivo, igual ao backend (today-30 a today)
  } else if (period === 'custom' && startDate && endDate) {
    curStart = parse(startDate);
    curEnd = parse(endDate);
    if (curEnd < curStart) return null;
  } else {
    return null;
  }

  const days = diffDays(curStart, curEnd);

  // custom >= 365 dias → YoY (ano a ano)
  const isYoY = period === 'custom' && days >= 365;

  if (isYoY) {
    const prevStart = new Date(curStart);
    const prevEnd = new Date(curEnd);
    prevStart.setFullYear(prevStart.getFullYear() - 1);
    prevEnd.setFullYear(prevEnd.getFullYear() - 1);
    return {
      current: { start: fmt(curStart), end: fmt(curEnd) },
      previous: { start: fmt(prevStart), end: fmt(prevEnd) },
      label: 'Mesmo período do ano anterior',
      isYearOverYear: true,
    };
  }

  // month → desloca 28 dias (4 semanas) para alinhar dia da semana.
  //   curStart = today-30 (31 dias), shift=28 → todos os dias têm par com mesmo dia-da-semana.
  // week  → desloca 7 dias (1 semana exata) para manter alinhamento de dia da semana
  // today/custom < 365 → desloca N dias imediatamente anteriores (período adjacente)
  const shiftDays = period === 'month' ? 28 : period === 'week' ? 7 : days;

  // Desloca start e end pelo mesmo número de dias → garante alinhamento de dia da semana
  const prevStartShift = new Date(curStart);
  prevStartShift.setDate(prevStartShift.getDate() - shiftDays);
  const prevEndShift = new Date(curEnd);
  prevEndShift.setDate(prevEndShift.getDate() - shiftDays);

  let label: string;
  if (period === 'today') label = 'Dia anterior';
  else if (period === 'week') label = '7 dias anteriores';
  else if (period === 'month') label = '4 semanas anteriores';
  else label = `${days} dias anteriores`;

  return {
    current: { start: fmt(curStart), end: fmt(curEnd) },
    previous: { start: fmt(prevStartShift), end: fmt(prevEndShift) },
    label,
    isYearOverYear: false,
  };
}

/**
 * Formata uma data ISO (YYYY-MM-DD) em "DD/MM/YYYY - Dia da Semana".
 */
export function formatDateWithWeekday(isoDate: string): string {
  const d = parse(isoDate);
  const weekdays = [
    'Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira',
    'Quinta-feira', 'Sexta-feira', 'Sábado',
  ];
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy} - ${weekdays[d.getDay()]}`;
}
